var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Log } from '../../../../log';
import { CommandSocket } from './CommandSocket';
import { applyDefaultCredentials } from './Credentials';
import { DriverAdapter } from './DriverAdapter';
import { DriverLogChannel } from './DriverLog';
import { DriverState } from './DriverState';
import { StateBridge } from './StateBridge';
import { timeouts as globalTimeouts } from './Timeouts';
export function accumulateResult(current, result) {
    if (current === 'authFailed') {
        if (result === 'verifyFailed')
            return result;
        return current;
    }
    else if (current === 'connectFailed') {
        return result;
    }
    else if (current === 'connected') {
        return current;
    }
    else if (current === 'verifyFailed') {
        if (result === 'connected')
            return result;
        return current;
    }
    throw new Error('unhandled initialization result');
}
export class SocketDriverAdapter extends DriverAdapter {
    constructor(equipmentId, driverName, Driver, ipAddress, simulate, onConnectionLost, onControlStatus, reconfigure, translate, logFactory) {
        super(Driver.key, Driver.emulatorKey, ipAddress, simulate, logFactory, translate);
        this.equipmentId = equipmentId;
        this.driverName = driverName;
        this.Driver = Driver;
        this.onConnectionLost = onConnectionLost;
        this.onControlStatus = onControlStatus;
        this.reconfigure = reconfigure;
        // bind callbacks
        this.onSocketConnectionLost = this.onSocketConnectionLost.bind(this);
    }
    computeInitialOfflineCaps(config) {
        var _a, _b;
        const socketOptionsArray = typeof this.Driver.socketOptions === 'function'
            ? this.Driver.socketOptions(config)
            : this.Driver.socketOptions;
        const socketOptions = socketOptionsArray[0];
        const emulatorKey = (_a = this.Driver.emulatorKey) !== null && _a !== void 0 ? _a : this.Driver.key;
        const socket = new CommandSocket(this.logFactory, socketOptions, this.simulate ? emulatorKey : undefined);
        this.state = new DriverState(this.logFactory, {});
        this.driver = new this.Driver(socket, this.state, new DriverLogChannel(Log.channel(this.driverName), this.ipAddress), this);
        (_b = this.stateBridge) === null || _b === void 0 ? void 0 : _b.destroy();
        this.stateBridge = new StateBridge(this.state, this.equipmentId, this.driver, socket, this.onControlStatus, this, this.logFactory);
        super.computeInitialOfflineCaps(config);
    }
    get isReconnecting() {
        return this.reconnectAttempt !== undefined;
    }
    get transport() {
        if (this.socket === undefined)
            throw new Error('socket is undefined');
        return this.socket;
    }
    reconnect(poptions) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info('attempting to reconnect');
            const options = {
                attemptDelayMs: Math.max((_a = poptions === null || poptions === void 0 ? void 0 : poptions.attemptDelayMs) !== null && _a !== void 0 ? _a : 0, 100),
                initialDelayMs: Math.max((_b = poptions === null || poptions === void 0 ? void 0 : poptions.initialDelayMs) !== null && _b !== void 0 ? _b : 0, 100),
                numAttempts: (_c = poptions === null || poptions === void 0 ? void 0 : poptions.numAttempts) !== null && _c !== void 0 ? _c : 1,
                onBeforeReconnect: poptions === null || poptions === void 0 ? void 0 : poptions.onBeforeReconnect,
            };
            if (this.socket === undefined)
                throw new Error('socket undefined');
            if (this.state === undefined)
                throw new Error('state undefined');
            if (this.reconnectAttempt) {
                this.log.info('reconnect attempt already in progress, waiting');
                while (this.reconnectAttempt) {
                    yield this.reconnectAttempt;
                }
                if (this.socket.state === 'connected') {
                    this.log.info('in-progress reconnect attempt successful');
                    return true;
                }
                this.log.debug('in-progress reconnect attempt failed, attempting to reconnect');
            }
            this.reconnectAttempt = this.reconnectInternal(options).then((reconnected) => {
                var _a, _b;
                this.reconnectAttempt = undefined;
                if (!reconnected && !((_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.options.allowNoConnection) !== null && _b !== void 0 ? _b : false)) {
                    this.onConnectionLost();
                }
                return reconnected;
            });
            return this.reconnectAttempt;
        });
    }
    resume() {
        var _a;
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.resume();
        super.resume();
    }
    suspend(delayMs, suspendType = 'all') {
        var _a;
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.suspend(delayMs === undefined ? undefined : delayMs + 100, suspendType === 'all' ? 'all' : 'idle');
        super.suspend(delayMs, suspendType);
    }
    doConfigure(config, token) {
        const _super = Object.create(null, {
            doConfigure: { get: () => super.doConfigure }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return;
            if (this.socket === undefined) {
                this.log.info(`doConfigure: socket not connected, nothing to do`);
                return;
            }
            if (this.socket.emulator !== undefined && this.socket.emulator.onConfigure !== undefined) {
                try {
                    this.socket.emulator.onConfigure(config);
                }
                catch (error) {
                    this.log.error(`error in emulator onConfigure: ${error.message}`);
                }
            }
            return _super.doConfigure.call(this, config, token);
        });
    }
    doConnect(credentials, config, storage, token) {
        const _super = Object.create(null, {
            doConnect: { get: () => super.doConnect }
        });
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return 'connectFailed';
            this.savedInitializationState = { credentials, config, storage }; // for use by reconnect()
            yield _super.doConnect.call(this, credentials, config, storage, token);
            if (token.isCancelled)
                return 'connectFailed';
            let result = 'connectFailed';
            const emulatorKey = (_a = this.Driver.emulatorKey) !== null && _a !== void 0 ? _a : this.Driver.key;
            const socketOptionsArray = typeof this.Driver.socketOptions === 'function'
                ? this.Driver.socketOptions(config)
                : this.Driver.socketOptions;
            for (const socketOptions of socketOptionsArray) {
                if (this.phase !== 'connecting')
                    this.phase = 'connecting';
                const addr = `${this.ipAddress}:${socketOptions.port}`;
                const sim = `${this.simulate ? ' (simulated)' : ''}`;
                const augmentedCredentials = applyDefaultCredentials(credentials, socketOptions.defaultCredentials);
                this.log.info(`trying connection to ${addr}${sim}`);
                try {
                    this.socket = new CommandSocket(this.logFactory, socketOptions, this.simulate ? emulatorKey : undefined);
                    this.socket.onConnectionLost(this.onSocketConnectionLost);
                    yield this.socket.connect(this.ipAddress, augmentedCredentials.username, augmentedCredentials.password);
                }
                catch (error) {
                    this.log.info(`failed connecting: ${error.message}`);
                    this.socket = undefined;
                    continue;
                }
                if (token.isCancelled)
                    return 'connectFailed';
                if (this.socket.state === 'connected') {
                    this.savedInitializationState.credentials = augmentedCredentials;
                    this.log.info(`connected to ${this.ipAddress}:${socketOptions.port}`);
                    if (this.simulate && ((_b = this.socket.emulator) === null || _b === void 0 ? void 0 : _b.onConfigure)) {
                        try {
                            this.socket.emulator.onConfigure(config);
                        }
                        catch (error) {
                            this.log.error(`error in emulator onConfigure: ${error.message}`);
                        }
                    }
                }
                else {
                    this.log.info(`failed connecting to ${this.ipAddress}:${socketOptions.port}, proceeding anyway`);
                }
                this.state = new DriverState(this.logFactory, storage);
                this.driver = new this.Driver(this.socket, this.state, new DriverLogChannel(Log.channel(this.driverName), this.ipAddress), this);
                (_c = this.stateBridge) === null || _c === void 0 ? void 0 : _c.destroy();
                this.stateBridge = new StateBridge(this.state, this.equipmentId, this.driver, this.socket, this.onControlStatus, this, this.logFactory);
                if (this.socket.options.allowNoConnection) {
                    this.stateBridge.canQueryWhenDisconnected = true;
                }
                this.log.info('initializing');
                const newResult = yield this.doInitialize(augmentedCredentials, config, storage, token);
                if (token.isCancelled)
                    return 'connectFailed';
                if (newResult !== 'connected') {
                    if (!['disconnecting', 'disconnected'].includes(this.socket.state)) {
                        yield this.doDisconnect(true, token);
                    }
                    this.socket = undefined;
                }
                result = accumulateResult(result, newResult);
                if (result === 'connected')
                    break;
                if (token.isCancelled)
                    return 'connectFailed';
            }
            if (result !== 'connected' &&
                this.phase !== 'disconnecting' &&
                this.phase !== 'disconnected') {
                this.phase = 'disconnected';
            }
            return result;
        });
    }
    doDisconnect(expected, token) {
        const _super = Object.create(null, {
            doDisconnect: { get: () => super.doDisconnect }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return;
            if (this.socket !== undefined) {
                yield _super.doDisconnect.call(this, expected, token);
                if (['disconnecting', 'disconnected'].includes(this.socket.state))
                    return;
                if (this.socket.emulator !== undefined &&
                    this.socket.emulator.onDisconnect !== undefined) {
                    this.socket.emulator.onDisconnect();
                }
                // socket.disconnect() resolves when the disconnection operation is sent, but the
                // socket isn't actually closed until the onSocketConnectionLost callback is called.
                yield this.socket.disconnect();
            }
        });
    }
    onSocketConnectionLost() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const prevPhase = this.phase;
            if ((prevPhase === 'controlling' && ((_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.options.allowNoConnection) !== null && _b !== void 0 ? _b : false)) ||
                this.reconnectAttempt !== undefined) {
                if (this.stateBridge === undefined)
                    throw new Error('stateBridge undefined');
                this.stateBridge.isConnected = false;
                return;
            }
            if (prevPhase !== 'disconnecting')
                yield this.disconnect(false);
            this.socket = undefined;
            this.phase = 'disconnected';
            if (prevPhase !== 'disconnecting') {
                this.onConnectionLost();
            }
        });
    }
    reconnectInternal(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.socket === undefined)
                throw new Error('socket undefined');
            if (this.stateBridge === undefined)
                throw new Error('stateBridge undefined');
            this.stateBridge.isConnected = false;
            this.suspend(options.initialDelayMs + options.numAttempts * options.attemptDelayMs + 30000);
            if (options.initialDelayMs > 0) {
                // Use globalTimeouts since DriverAdapter calls clearAll when disconnecting.
                yield globalTimeouts.delay(options.initialDelayMs);
            }
            let reconnected = false;
            for (let i = 0; i < options.numAttempts; i++) {
                if (options.onBeforeReconnect)
                    yield options.onBeforeReconnect();
                if (i !== 0 && options.attemptDelayMs > 0) {
                    // Use globalTimeouts since DriverAdapter calls clearAll when disconnecting.
                    yield globalTimeouts.delay(options.attemptDelayMs);
                }
                this.log.info(`attempting reconnect ${i + 1}/${options.numAttempts}`);
                yield this.socket.reconnect();
                reconnected = this.socket.state === 'connected';
                if (reconnected) {
                    this.log.info(`reconnected`);
                    break;
                }
            }
            if (reconnected) {
                this.resume();
                this.isReconnected = true;
                if (this.savedInitializationState === undefined) {
                    throw new Error(`savedInitializationState undefined`);
                }
                const result = yield this.initialize(this.savedInitializationState.credentials, this.savedInitializationState.config, this.savedInitializationState.storage);
                this.stateBridge.isConnected = true;
                if (result !== 'connected') {
                    if (!['disconnecting', 'disconnected'].includes(this.socket.state)) {
                        yield this.socket.disconnect();
                    }
                    return false;
                }
                yield this.reconfigure();
            }
            else {
                this.onSocketConnectionLost();
            }
            return reconnected;
        });
    }
}
